import { Component, ViewChild, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { isExecutionResult, isHttpResponseError } from "@tylertech/core-module";
import { CustomViewService } from "@tylertech/custom-view";
import { MetadataErrorService, RepositoryFormData } from "@tylertech/metadata-module";
import { IInfoCardData, IInfoCardSaveCriteria, MetadataVerification, } from "@tylertech/tyler-api-types";

import { InfoCardService } from "../../services";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { NgForm } from "@angular/forms";

export interface IEditDialogCustomViewBinding<T = unknown>
{
    readonly entity: T;
    readonly providerId: string;
}

export interface EditDialogData
{
    readonly addMode: boolean;
    /** @deprecated  */
    readonly editComponent?: string;
    readonly item: IInfoCardData;
}

@Component({
    selector: "edit-dialog-component",
    templateUrl: "./edit.dialog.tmpl.html",
    standalone: false
})
export class EditDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<EditDialogComponent, IInfoCardData>>(MatDialogRef);
    private readonly data = inject<EditDialogData>(MAT_DIALOG_DATA);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $infoCardService = inject(InfoCardService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly $customViewService = inject(CustomViewService);

    hasCustomView: boolean = false;
    customDataBinding: IEditDialogCustomViewBinding;

    @ViewChild("form") form: NgForm;
    loading: boolean;

    private get addMode(): boolean { return this.data.addMode; }
    get item(): IInfoCardData { return this.data.item; }
    get editComponent(): string { return this.data.editComponent; }

    get Title(): string
    {
        return this.addMode ? "Add Item" : "Edit Item";
    }

    get CanSave(): boolean
    {
        return !this.form?.invalid;
    }

    get CustomEdit(): boolean
    {
        return !!this.editComponent && this.item.Entity;
    }

    ngOnInit(): void
    {
        this.dialogRef.updateSize("50vw");

        this.hasCustomView = !this.$customViewService.hasView(this.editComponent);
        if (!this.hasCustomView)
        {
            this.customDataBinding = {
                entity: this.item.Entity,
                providerId: this.item.CardProviderId,
            };
        }
    }

    save(): void
    {
        this.loading = true;
        const criteria: IInfoCardSaveCriteria = {
            CardProviderId: this.item.CardProviderId,
            InfoCardData: this.item,
            DeleteItem: undefined,
            ReturnCriteria: undefined
        };
        this.$infoCardService.Save(criteria)
            .then(
                result =>
                {
                    if (result)
                    {
                        this.dialogRef.close(result);
                    }
                },
                (error: unknown) =>
                {
                    if (!isHttpResponseError(error))
                    {
                        throw new Error();
                    }

                    const errorInfo = error.error;
                    if (errorInfo.AdditionalInfo)
                    {
                        const info: MetadataVerification = JSON.parse(errorInfo.AdditionalInfo);
                        const dataController = new RepositoryFormData(info.Type, this.item.Entity);
                        this.$metadataErrorService.setErrors(info, dataController);
                        const errors = this.$metadataErrorService.getErrorList(info);

                        return this.$commonDialogsService.showAlertHtmlMessage(
                            errors.reduce((p, x) => `${p}<li>${x}</li>`, ""),
                            "Verification Errors",
                            "Acknowledge");
                    }

                    const msg = getErrorMessage(error);
                    return this.$commonDialogsService.showAlertHtmlMessage(
                        `Failed to save due to the following: <br/>${msg}`,
                        "Save Failed!");
                }
            )
            .finally(() =>
            {
                this.loading = false;
            });

        function getErrorMessage(error: unknown): string
        {
            if (isHttpResponseError(error) && error.error.Message)
            {
                return error.error.Message;
            }

            if (isExecutionResult(error) && Array.isArray(error.Result) && error.Result.length)
            {
                return error.Result.join("<br />");
            }

            return "An Error Occurred.";
        }
    }
}
